<script setup lang="ts" generic="T extends Entity">
import { computed, ref } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import SharedEntityDrawer from "./SharedEntityDrawer.vue";
import { useConfirm } from "primevue/useconfirm";
import { SchemaType } from "src/enums/schema-types";
import type { Entity } from "src/types/entities/entity-type";
import type { EntitySchema } from "src/types/entity-schema";
import type { CrudTableColumn, CrudTableColumnOptions } from "src/router/types";
import SelectEntity from "../form/SelectEntity.vue";

const props = defineProps<{
  parentId: string;
  header: string;
  data: T[];
  schema: EntitySchema;
  options?: CrudTableColumnOptions;
}>();

const emit = defineEmits<{
  delete: [item: T];
  add: [];
}>();

const visible = defineModel<boolean>("visible", { required: true });

const confirm = useConfirm();

const columns = computed(() => {
  if (props.data.length > 0) {
    return Object.keys(props.data[0]).filter((key) => key !== "id");
  }
  return [];
});

const isDrawerVisible = ref(false);
const selectedItem = ref<Entity | null>(null);
const selectedEntity = ref<Entity | null>(null);
const { mutateAsync: addMutateAsync } = props.options?.requests?.add?.() ?? {};

const entityRoute = computed(() => {
  return props.schema.tableColumnsDefault.find((column) => column.name === "entityName");
});

const entitySchema = computed<EntitySchema>(() => {
  return {
    entity: props.header ?? "",
    ...props.options,
  };
});

function onClickDelete(item: T) {
  confirm.require({
    message: "Are you sure you want to delete this item?",
    header: "Confirmation",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      emit("delete", item);
    },
  });
}

async function onClickAdd() {
  if (addMutateAsync) {
    await addMutateAsync({ id: props.parentId, data: {
      [props.header]: [selectedEntity.value],
      },
    });
    emit("add");
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    :header="header"
    :modal="true"
    :style="{ width: '50vw' }"
    @update:visible="(val) => (visible = val)"
  >
    <div class="mb-3" v-if="props.options?.crud?.add">
      <SelectEntity
        v-model="selectedEntity"
        :entity="entitySchema.entity"
        :requests="props.options?.requests"
      >
        <template #actions>
          <Button icon="pi pi-plus" label="Add" @click="onClickAdd" :disabled="!selectedEntity" />
        </template>
      </SelectEntity>
    </div>
    <DataTable :value="data">
      <template v-for="col in columns" :key="col">
        <Column :field="col" :header="col" />
      </template>
      <Column header="Actions" v-if="props.options?.crud?.delete">
        <template #body="slotProps">
          <Button icon="pi pi-trash" class="p-button-danger p-button-text" @click="onClickDelete(slotProps.data)" />
        </template>
      </Column>
    </DataTable>
  </Dialog>
</template>
